/**
 * Sanitizes HTML intended for emails to ensure high compatibility with strict email clients like Gmail.
 * - Removes modern CSS layouts not supported by email clients (float, flex, grid, position, clear).
 * - Strips Microsoft Word / MSO junk styles.
 * - Unwraps empty/redundant inline wrappers that cause words to break across lines.
 */
export const cleanEmailHtml = (rawHtml) => {
    if (!rawHtml || typeof rawHtml !== 'string') return rawHtml;

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, 'text/html');

        // 1. Remove unsupported CSS properties from style attributes
        const styledElements = doc.querySelectorAll('[style]');
        styledElements.forEach((el) => {
            const style = el.getAttribute('style');
            if (style) {
                const cleanedStyle = style
                    .replace(/float\s*:\s*[^;]+;?/gi, '')
                    .replace(/display\s*:\s*(flex|grid|inline-flex|inline-grid)[^;]*;?/gi, '')
                    .replace(/position\s*:\s*[^;]+;?/gi, '')
                    .replace(/clear\s*:\s*[^;]+;?/gi, '')
                    .replace(/mso-[^:]+:[^;]+;?/gi, '')
                    .trim();

                if (cleanedStyle) {
                    el.setAttribute('style', cleanedStyle);
                } else {
                    el.removeAttribute('style');
                }
            }
        });

        // 2. Unwrap non-semantic wrappers that have no styling or class
        // (often introduced when pasting from external rich text / web pages)
        const wrappers = doc.querySelectorAll('p > span, p > div, div > span');
        wrappers.forEach((el) => {
            const hasStyle = el.getAttribute('style');
            const hasClass = el.getAttribute('class');
            if (!hasStyle && !hasClass) {
                el.replaceWith(...el.childNodes);
            }
        });

        // 3. Convert <div> directly inside <p> by unwrapping (valid HTML)
        const divsInP = doc.querySelectorAll('p > div');
        divsInP.forEach((div) => {
            div.replaceWith(...div.childNodes);
        });

        return doc.body.innerHTML;
    } catch (error) {
        console.error('Error in cleanEmailHtml:', error);
        return rawHtml;
    }
};
